import { useMemo, useState } from "react";
import { Users, Calendar, UserCheck, ListChecks } from "lucide-react";
import { UserAvatar } from "../../components/ui/avatar";
import { UserIdentity } from "../../components/UserIdentity";
import { SessionCard } from "../dashboard/components/SessionCard";
import { BasicPollCard } from "../../components/polls/basic-poll-card";
import { GroupBasicPollModal } from "../dashboard/components/group-basic-poll-modal";
import { PaginationFooter } from "../../components/ui/pagination-footer";
import { usePagination } from "../../hooks/use-pagination";
import { PAGE_SIZE_DEFAULT } from "../../lib/pagination";
import { useAuth } from "../../app/useAuth";
import {
  canViewOtherVotesPublicly,
  getVoteIdentityDisplayMode,
} from "../../lib/vote-visibility";

/**
 * Returns the sort key timestamp for a poll.
 * Finalized → winning slot start.
 * Open/pending → earliest slot start (ties broken by latest slot start).
 */
function getPollSortDate(poll) {
  if (poll.winningSlot?.start) {
    return { primary: new Date(poll.winningSlot.start).getTime(), secondary: 0 };
  }
  const starts = (poll.slots || [])
    .map((s) => (s.start ? new Date(s.start).getTime() : null))
    .filter((t) => t != null);
  if (starts.length === 0) return { primary: 0, secondary: 0 };
  return { primary: Math.min(...starts), secondary: Math.max(...starts) };
}

const POLL_TABS = [
  { key: "session", label: "Session Polls", Icon: Calendar },
  { key: "general", label: "General Polls", Icon: ListChecks },
];

/**
 * Shared content for friend profile (used by both modal and page).
 * Shows shared polls, shared groups, and mutual friends.
 * PRIVACY: Only displays items where both users are participants.
 */
export function SharedProfileContent({
  sharedPolls = [],
  sharedGroups = [],
  sharedBasicPolls = [],
  mutualFriendProfiles = [],
  getGroupColor,
  groupsById = {},
  onFriendClick,
}) {
  const [activeTab, setActiveTab] = useState("session");
  const [activeGroupPoll, setActiveGroupPoll] = useState(null);
  const { user: currentUser } = useAuth();

  const sortedPolls = useMemo(() => {
    return [...sharedPolls].sort((a, b) => {
      const aDate = getPollSortDate(a);
      const bDate = getPollSortDate(b);
      if (bDate.primary !== aDate.primary) return bDate.primary - aDate.primary;
      return bDate.secondary - aDate.secondary;
    });
  }, [sharedPolls]);

  const activePolls = activeTab === "session" ? sortedPolls : sharedBasicPolls;
  const pollPager = usePagination(activePolls, {
    pageSize: PAGE_SIZE_DEFAULT,
    resetKey: activeTab,
  });

  return (
    <div className="mt-4 space-y-4">
      {/* Poll Tabs */}
      <section>
        <div className="flex gap-1 rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800">
          {POLL_TABS.map(({ key, label, Icon }) => {
            const count = key === "session" ? sharedPolls.length : sharedBasicPolls.length;
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isActive
                    ? "bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100"
                    : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label} ({count})
              </button>
            );
          })}
        </div>

        {/* Session Polls Tab */}
        {activeTab === "session" && (
          <>
            {sharedPolls.length === 0 ? (
              <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                No shared session polls.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {pollPager.pageItems.map((poll) => (
                  <SessionCard
                    key={poll.id}
                    scheduler={poll}
                    isArchived={false}
                    canManage={false}
                    groupColor={getGroupColor?.(poll.questingGroupId) ?? null}
                    questingGroup={groupsById[poll.questingGroupId] ?? null}
                    winningSlot={poll.winningSlot ?? null}
                    slots={poll.slots || []}
                    participants={poll.effectiveParticipants || []}
                    voters={poll.voters || []}
                    submittedVoteCount={poll.submittedVoteCount}
                    totalParticipantCount={poll.totalParticipantCount}
                    attendanceSummary={poll.attendanceSummary ?? null}
                    displayTimeZone={poll.displayTimeZone ?? null}
                    showTimeZone={poll.showTimeZone ?? true}
                    voteIdentityDisplayMode={getVoteIdentityDisplayMode({
                      isCreator: false,
                      hideVoterIdentities: poll?.hideVoterIdentities,
                      voteAnonymization: poll?.voteAnonymization,
                      detailsRevealed: canViewOtherVotesPublicly({
                        voteVisibility: poll?.voteVisibility,
                        allParticipantsVoted: poll?.votesAllSubmitted === true,
                        isFinalized: poll?.status === "FINALIZED",
                      }),
                    })}
                    currentUser={currentUser}
                  />
                ))}
                <PaginationFooter
                  page={pollPager.page}
                  pageCount={pollPager.pageCount}
                  rangeStart={pollPager.rangeStart}
                  rangeEnd={pollPager.rangeEnd}
                  total={pollPager.total}
                  hasPrev={pollPager.hasPrev}
                  hasNext={pollPager.hasNext}
                  onPrev={pollPager.goPrev}
                  onNext={pollPager.goNext}
                  label="Shared session polls"
                  itemNoun="poll"
                />
              </div>
            )}
          </>
        )}

        {/* General Polls Tab */}
        {activeTab === "general" && (
          <>
            {sharedBasicPolls.length === 0 ? (
              <p className="mt-3 text-xs text-slate-400 dark:text-slate-500">
                No shared general polls.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                {pollPager.pageItems.map((poll) => (
                  <BasicPollCard
                    key={`${poll.parentType}:${poll.parentId}:${poll.pollId}`}
                    poll={poll}
                    showActions={false}
                    onOpen={
                      poll.parentType === "group"
                        ? () => setActiveGroupPoll({ groupId: poll.parentId, pollId: poll.pollId })
                        : undefined
                    }
                  />
                ))}
                <PaginationFooter
                  page={pollPager.page}
                  pageCount={pollPager.pageCount}
                  rangeStart={pollPager.rangeStart}
                  rangeEnd={pollPager.rangeEnd}
                  total={pollPager.total}
                  hasPrev={pollPager.hasPrev}
                  hasNext={pollPager.hasNext}
                  onPrev={pollPager.goPrev}
                  onNext={pollPager.goNext}
                  label="Shared general polls"
                  itemNoun="poll"
                />
              </div>
            )}
          </>
        )}
      </section>

      {/* Shared Groups */}
      <section>
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <Users className="h-3.5 w-3.5" />
          Shared Groups ({sharedGroups.length})
        </h4>
        {sharedGroups.length === 0 ? (
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
            No shared groups.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {sharedGroups.map((group) => {
              const color = getGroupColor?.(group.id);
              return color ? (
                <span
                  key={group.id}
                  className="flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
                  style={{ backgroundColor: color }}
                >
                  <Users className="h-3 w-3" />
                  {group.name}
                </span>
              ) : (
                <span
                  key={group.id}
                  className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                >
                  {group.name}
                </span>
              );
            })}
          </div>
        )}
      </section>

      {/* Mutual Friends */}
      <section>
        <h4 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          <UserCheck className="h-3.5 w-3.5" />
          Friends in Common ({mutualFriendProfiles.length})
        </h4>
        {mutualFriendProfiles.length === 0 ? (
          <p className="mt-2 text-xs text-slate-400 dark:text-slate-500">
            No mutual friends.
          </p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-2">
            {mutualFriendProfiles.map((friend) => {
              const content = (
                <>
                  <UserAvatar
                    user={friend}
                    email={friend.email}
                    src={friend.avatar}
                    size={20}
                  />
                  <span className="font-medium text-slate-600 dark:text-slate-300">
                    <UserIdentity user={friend} showIdentifier={false} />
                  </span>
                </>
              );
              return onFriendClick ? (
                <button
                  type="button"
                  key={friend.email}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1.5 text-xs hover:bg-slate-100 transition-colors dark:bg-slate-800 dark:hover:bg-slate-700"
                  onClick={() => onFriendClick(friend)}
                >
                  {content}
                </button>
              ) : (
                <div
                  key={friend.email}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full bg-slate-50 px-3 py-1.5 text-xs dark:bg-slate-800"
                >
                  {content}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* In-place Group Basic Poll Modal */}
      {activeGroupPoll && (
        <GroupBasicPollModal
          groupId={activeGroupPoll.groupId}
          pollId={activeGroupPoll.pollId}
          onClose={() => setActiveGroupPoll(null)}
        />
      )}
    </div>
  );
}
